import React, {useEffect, useState} from 'react';
import Joyride, {Step} from 'react-joyride';
import {inject, observer} from 'mobx-react';
import {useLocation} from 'react-router-dom';
import {waitForTourTargets} from '../utils/tourChecker';
import {useGlobalTour} from '../utils/useGlobalTour';
import {TourSection} from '../utils/tourStorage';

import {getDashboardSteps} from '../view/steps/dashboardSteps';
import {getAnalyticsSteps} from '../view/steps/analyticsSteps';
import {getCoursesSteps} from '../view/steps/coursesSteps';
import {getLibrarySteps} from '../view/steps/librarySteps';
import {getMicrolessonsSteps} from '../view/steps/microlessonsSteps';
import {getMicrolessonsListSteps} from '../view/steps/microlessonsListSteps';
import {getAiHomeSteps} from '../view/steps/aiHomeSteps';
import {getLearnersSteps} from '../view/steps/learnersSteps';
import {getCreateLearnersSteps} from '../view/steps/createLearnersSteps';
import {getSettingsGeneralSteps} from '../view/steps/settingsGeneralSteps';
import TourTooltip from '../view/tooltip/TourTooltip';
import NavigationStore from '../../../navigation/store/NavigationStore';
import {ORGANIZATION_USER_STAFF} from '../../../data/models/Oganization/Organization';
import {APP_PERMISSIONS} from '../../../data/permissions';
import '../styles.css';

interface Props {
  section: TourSection;
  navigationStore?: NavigationStore;
}

const stepMap: Record<TourSection, () => Step[]> = {
  dashboard: getDashboardSteps,
  analytics: getAnalyticsSteps,
  courses: getCoursesSteps,
  library: getLibrarySteps,
  microlessons: getMicrolessonsSteps,
  microlessonsList: getMicrolessonsListSteps,
  aiHome: getAiHomeSteps,
  learners: getLearnersSteps,
  createLearners: getCreateLearnersSteps,
  settingsGeneral: getSettingsGeneralSteps,
};

const isTargetVisible = (selector: string): boolean => {
  const el = document.querySelector(selector);
  return el instanceof HTMLElement && el.offsetParent !== null;
};

const applyTargetFallbacks = (steps: Step[]): Step[] =>
  steps.map(step => {
    if (typeof step.target !== 'string' || isTargetVisible(step.target)) {
      return step;
    }
    return {
      ...step,
      target: 'body',
      placement: 'center',
      disableBeacon: true,
    };
  });

function TourContainer({section, navigationStore}: Props) {
  const {runTour, tourStepIndex, handleJoyrideCallback} =
    useGlobalTour(section);
  const location = useLocation();
  const [steps, setSteps] = useState<Step[]>([]);
  const [canRun, setCanRun] = useState(false);

  const isExcludedRoute =
    location.pathname.startsWith('/editor') ||
    location.pathname.startsWith('/preview');

  const canShowTour =
    !isExcludedRoute &&
    navigationStore?.userOrganization?.userType === ORGANIZATION_USER_STAFF &&
    navigationStore?.userPermissions.includes(
      APP_PERMISSIONS.ORG_MANAGE_SETTINGS,
    );

  useEffect(() => {
    setCanRun(false);
    setSteps([]);

    if (!runTour || !canShowTour) return;

    const rawSteps = stepMap[section]();
    if (rawSteps.length === 0) return;

    const selectors = rawSteps
      .map(step => step.target)
      .filter((target): target is string => typeof target === 'string');

    let cancelled = false;
    const start = () => {
      if (cancelled) return;
      setSteps(applyTargetFallbacks(rawSteps));
      setCanRun(true);
    };

    waitForTourTargets(selectors, 3000).then(start).catch(start);

    return () => {
      cancelled = true;
    };
  }, [runTour, section, canShowTour]);

  if (!canRun || steps.length === 0) return null;

  return (
    <Joyride
      steps={steps}
      run={canRun}
      stepIndex={tourStepIndex}
      continuous
      showSkipButton
      showProgress
      callback={handleJoyrideCallback}
      tooltipComponent={TourTooltip}
      styles={{
        options: {
          zIndex: 950,
          arrowColor: '#ffffff',
          overlayColor: 'rgba(0, 0, 0, 0.6)',
          primaryColor: '#000',
        },
      }}
    />
  );
}

export default inject('navigationStore')(observer(TourContainer));
